<template>
  <div class="ap-field ap-datepicker-field" ref="root">
    <label>{{ label }} <span v-if="required" class="ap-req">*</span></label>

    <div class="ap-input-wrap ap-datepicker-trigger" :class="{ 'is-open': showCalendar }" @click="toggleCalendar">
      <fa-icon :icon="['fas', 'calendar-alt']" class="ap-lead" />
      <span class="ap-datepicker-val" :class="{ 'is-placeholder': !value }">
        {{ value || 'DD / MM / YYYY' }}
      </span>
      <fa-icon
        :icon="['fas', 'chevron-down']"
        class="ap-datepicker-arrow"
        :class="{ 'is-open': showCalendar }"
      />
    </div>

    <!--
      Portal: rendered here by Vue (reactivity/refs work normally),
      moved to <body> in mounted() — position:fixed is then always relative
      to the true viewport, immune to any ancestor CSS transform.
    -->
    <div v-show="showCalendar" ref="calendar" class="ap-calendar" :style="calendarStyle" @click.stop>

      <!-- Month / Year nav -->
      <div class="ap-cal-nav">
        <button type="button" class="ap-cal-nav-btn" @click="prevMonth">
          <fa-icon :icon="['fas', 'chevron-left']" />
        </button>
        <div class="ap-cal-selects">
          <select v-model.number="calMonth" class="ap-cal-sel">
            <option v-for="(m, i) in MONTHS" :key="i" :value="i">{{ m }}</option>
          </select>
          <select v-model.number="calYear" class="ap-cal-sel">
            <option v-for="y in yearRange" :key="y" :value="y">{{ y }}</option>
          </select>
        </div>
        <button type="button" class="ap-cal-nav-btn" @click="nextMonth">
          <fa-icon :icon="['fas', 'chevron-right']" />
        </button>
      </div>

      <!-- Weekday headers -->
      <div class="ap-cal-grid">
        <div v-for="d in DAYS" :key="d" class="ap-cal-weekday">{{ d }}</div>

        <!-- Leading empty cells -->
        <div
          v-for="n in leadingBlanks"
          :key="'e' + n"
          class="ap-cal-cell"
        ></div>

        <!-- Day buttons -->
        <button
          v-for="day in daysInMonth"
          :key="day"
          type="button"
          class="ap-cal-cell ap-cal-day"
          :class="{
            'is-selected': isSelected(day),
            'is-today': isToday(day),
            'is-disabled': isDayDisabled(day)
          }"
          :disabled="isDayDisabled(day)"
          @click="selectDay(day)"
        >{{ day }}</button>
      </div>

      <!-- Footer -->
      <div class="ap-cal-footer">
        <button type="button" class="ap-cal-today" @click="selectToday">Today</button>
        <button type="button" class="ap-cal-clear" @click="clearDate">Clear</button>
      </div>

    </div>
  </div>
</template>

<script>
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December']
const DAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']

export default {
  name: 'DateInput',
  props: {
    value: { type: String, default: '' },
    label: { type: String, default: 'Date' },
    required: { type: Boolean, default: false },
    minDate: { type: String, default: '' },
    maxDate: { type: String, default: '' }
  },
  data () {
    const now = new Date()
    return {
      MONTHS,
      DAYS,
      showCalendar: false,
      calMonth: now.getMonth(),
      calYear: now.getFullYear(),
      calendarStyle: {}
    }
  },
  computed: {
    daysInMonth () {
      return new Date(this.calYear, this.calMonth + 1, 0).getDate()
    },
    leadingBlanks () {
      return new Date(this.calYear, this.calMonth, 1).getDay()
    },
    yearRange () {
      const base = new Date().getFullYear()
      const years = []
      for (let y = base - 100; y <= base + 20; y++) years.push(y)
      return years
    }
  },
  watch: {
    value: {
      immediate: true,
      handler (val) {
        if (!val) return
        const [d, m, y] = val.split('/')
        if (d && m && y && !isNaN(+y)) {
          this.calMonth = parseInt(m) - 1
          this.calYear = parseInt(y)
        }
      }
    }
  },
  mounted () {
    document.addEventListener('click', this.onOutsideClick)
    window.addEventListener('resize', this.closeCalendar)
    window.addEventListener('scroll', this.onScroll, true)

    // Portal: move calendar out of the component tree into <body>.
    // position:fixed on .ap-calendar is then always relative to the true
    // viewport — immune to any ancestor CSS transform / overflow.
    document.body.appendChild(this.$refs.calendar)
  },
  beforeDestroy () {
    document.removeEventListener('click', this.onOutsideClick)
    window.removeEventListener('resize', this.closeCalendar)
    window.removeEventListener('scroll', this.onScroll, true)

    const cal = this.$refs.calendar
    if (cal && cal.parentNode === document.body) {
      document.body.removeChild(cal)
    }
  },
  methods: {
    toggleCalendar () {
      this.showCalendar = !this.showCalendar
      if (this.showCalendar) this.$nextTick(this.positionCalendar)
    },
    positionCalendar () {
      const trigger = this.$el && this.$el.querySelector('.ap-datepicker-trigger')
      if (!trigger) return
      const r = trigger.getBoundingClientRect()
      this.calendarStyle = {
        position: 'fixed',
        top: (r.bottom + 6) + 'px',
        left: r.left + 'px',
        zIndex: 9999
      }
    },
    closeCalendar () {
      this.showCalendar = false
    },
    onScroll () {
      if (this.showCalendar) this.positionCalendar()
    },
    onOutsideClick (e) {
      // Check both the trigger root AND the portalled calendar (now a body child)
      const root = this.$refs.root
      const cal = this.$refs.calendar
      const insideRoot = root && root.contains(e.target)
      const insideCal = cal && cal.contains(e.target)
      if (!insideRoot && !insideCal) {
        this.showCalendar = false
      }
    },
    prevMonth () {
      if (this.calMonth === 0) {
        this.calMonth = 11
        this.calYear--
      } else {
        this.calMonth--
      }
    },
    nextMonth () {
      if (this.calMonth === 11) {
        this.calMonth = 0
        this.calYear++
      } else {
        this.calMonth++
      }
    },
    selectDay (day) {
      const dd = String(day).padStart(2, '0')
      const mm = String(this.calMonth + 1).padStart(2, '0')
      this.$emit('input', `${dd}/${mm}/${this.calYear}`)
      this.showCalendar = false
    },
    selectToday () {
      const now = new Date()
      this.calMonth = now.getMonth()
      this.calYear = now.getFullYear()
      this.selectDay(now.getDate())
    },
    clearDate () {
      this.$emit('input', '')
      this.showCalendar = false
    },
    isSelected (day) {
      if (!this.value) return false
      const [d, m, y] = this.value.split('/')
      return parseInt(d) === day &&
        parseInt(m) - 1 === this.calMonth &&
        parseInt(y) === this.calYear
    },
    isToday (day) {
      const now = new Date()
      return day === now.getDate() &&
        this.calMonth === now.getMonth() &&
        this.calYear === now.getFullYear()
    },
    isDayDisabled (day) {
      const cell = new Date(this.calYear, this.calMonth, day)
      if (this.minDate) {
        const [md, mm, my] = this.minDate.split('/')
        if (md && mm && my) {
          const min = new Date(parseInt(my), parseInt(mm) - 1, parseInt(md))
          if (cell < min) return true
        }
      }
      if (this.maxDate) {
        const [md, mm, my] = this.maxDate.split('/')
        if (md && mm && my) {
          const max = new Date(parseInt(my), parseInt(mm) - 1, parseInt(md))
          if (cell > max) return true
        }
      }
      return false
    }
  }
}
</script>
